import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAudienceKpiCounts, type AudienceKpiCounts } from '../utils/audience-kpi-counts';
import type { CampaignAudienceSelection } from '../utils/campaign-audience.utils';

const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);

  return debouncedValue;
};

export const useAudienceKpiCounts = (
  selection: Pick<
    CampaignAudienceSelection,
    'includeListIds' | 'includeSegmentIds' | 'excludeListIds' | 'excludeSegmentIds'
  >,
  options: { enabled?: boolean; campaignId?: string } = {}
) => {
  const includeListIds = [...selection.includeListIds]
    .map((id) => id.trim())
    .filter(Boolean)
    .sort();
  const includeSegmentIds = [...selection.includeSegmentIds]
    .map((id) => id.trim())
    .filter(Boolean)
    .sort();
  const excludeListIds = [...(selection.excludeListIds ?? [])].sort();
  const excludeSegmentIds = [...selection.excludeSegmentIds].sort();
  const campaignId = options.campaignId?.trim() || '';

  const debouncedListIds = useDebouncedValue(includeListIds, 300);
  const debouncedSegmentIds = useDebouncedValue(includeSegmentIds, 300);

  const selectionReady =
    includeListIds.length === debouncedListIds.length &&
    includeSegmentIds.length === debouncedSegmentIds.length &&
    includeListIds.every((id, index) => id === debouncedListIds[index]) &&
    includeSegmentIds.every((id, index) => id === debouncedSegmentIds[index]);

  const enabled =
    (options.enabled ?? true) &&
    selectionReady &&
    (debouncedListIds.length > 0 || debouncedSegmentIds.length > 0);

  // Use the base query hook so KPI failures stay inline instead of triggering global error toasts.
  return useQuery<AudienceKpiCounts>({
    queryKey: [
      'audience-kpi-counts',
      campaignId,
      debouncedListIds,
      debouncedSegmentIds,
      excludeListIds,
      excludeSegmentIds,
    ],
    queryFn: () =>
      fetchAudienceKpiCounts({
        campaignId: campaignId || undefined,
        includeListIds: debouncedListIds,
        includeSegmentIds: debouncedSegmentIds,
        excludeListIds,
        excludeSegmentIds,
      }),
    staleTime: 15 * 1000,
    enabled,
  });
};
